import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { 
  Bars3Icon, 
  XMarkIcon, 
  BuildingOfficeIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  BellIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  UserCircleIcon,
  SunIcon,
  CommandLineIcon,
  AcademicCapIcon,
  UsersIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import {
  getNavigationByRole,
  getPortalNameByRole,
  type UserRole
} from './NavigationConfig';
import toast from 'react-hot-toast';
import { NotificationBell } from '@/components/communication';

interface DashboardLayoutProps {
  children: React.ReactNode;
}


const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const { user, logout } = useAuth();
  const { userRole, isLoading: roleLoading } = useUserRole();
  const location = useLocation();

  // Search items - empty until API is implemented
  const searchableItems: { id: number; type: string; title: string; subtitle: string; href: string }[] = [];

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.trim() === '') {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const filteredResults = searchableItems.filter(item =>
      item.title.toLowerCase().includes(query.toLowerCase()) ||
      item.subtitle.toLowerCase().includes(query.toLowerCase())
    );

    setSearchResults(filteredResults);
    setShowSearchResults(true);
  };

  const handleSearchFocus = () => {
    setSearchFocused(true);
    if (searchQuery.trim() !== '') {
      setShowSearchResults(true);
    }
  };

  const handleSearchBlur = () => {
    setTimeout(() => {
      setSearchFocused(false);
      setShowSearchResults(false);
    }, 200);
  };

  const getSearchIcon = (type: string) => {
    switch (type) {
      case 'tenant':
        return BuildingOfficeIcon;
      case 'user':
        return UsersIcon;
      case 'analytics':
        return Cog6ToothIcon;
      case 'system':
        return Cog6ToothIcon;
      default:
        return DocumentTextIcon;
    }
  };

  const getSearchPlaceholder = (role: UserRole): string => {
    switch (role) {
      case 'super_admin':
        return 'Search institutions, users, analytics...';
      case 'school_admin':
        return 'Search students, teachers, classes...';
      case 'teacher':
        return 'Search students, classes, assignments...';
      case 'parent':
        return 'Search child activities, teachers, events...';
      case 'driver':
        return 'Search students, routes, schedules...';
      case 'student':
        return 'Search classes, assignments, events...';
      default:
        return 'Search...';
    }
  };


  // Use the actual role from the API via useUserRole hook
  const navigationSections = getNavigationByRole(userRole);
  const portalName = getPortalNameByRole(userRole);

  // Get role-based profile URL
  const getProfileUrl = (role: UserRole): string => {
    switch (role) {
      case 'super_admin':
        return '/admin/profile';
      case 'school_admin':
        return '/schoolAdmin/profile';
      case 'teacher':
        return '/teacher/profile';
      case 'parent':
        return '/parent/profile';
      case 'driver':
        return '/driver/profile';
      case 'student':
        return '/student/profile';
      default:
        return '/admin/profile';
    }
  };

  const getSettingsUrl = (role: UserRole): string => {
    switch (role) {
      case 'super_admin':
        return '/admin/settings';
      case 'school_admin':
        return '/schoolAdmin/system-settings';
      default:
        return '/admin/settings';
    }
  };

  const get2FAUrl = (role: UserRole): string => {
    switch (role) {
      case 'super_admin':
        return '/admin/2fa';
      case 'school_admin':
        return '/schoolAdmin/2fa';
      case 'teacher':
        return '/teacher/2fa';
      case 'parent':
        return '/parent/2fa';
      case 'driver':
        return '/driver/2fa';
      case 'student':
        return '/student/2fa';
      default:
        return '/admin/2fa';
    }
  };

  // Show loading spinner while role is being determined
  if (roleLoading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)}></div>
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                onClick={() => setSidebarOpen(false)}
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              >
                <XMarkIcon className="h-6 w-6 text-white" />
              </button>
            </div>
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto custom-scrollbar">
              <div className="flex-shrink-0 flex items-center px-4">
                <div className="flex items-center space-x-3">
                  <div className="dashboard-stat-icon bg-gradient-to-br from-primary-600 to-indigo-700">
                    <AcademicCapIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <span className="text-xl font-bold gradient-text">SmartTag School</span>
                    <p className="text-xs text-gray-500 -mt-1">{portalName}</p>
                  </div>
                </div>
              </div>
              <nav className="mt-6 px-2">
                {navigationSections.map((section) => (
                  <div key={section.title} className="mb-6">
                    <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      {section.title}
                    </h3>
                    <div className="space-y-1">
                      {section.items.map((item) => {
                        const IconComponent = item.icon;
                        const isCurrentPage = location.pathname === item.href;
                        
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`sidebar-nav-item ${
                              isCurrentPage ? 'sidebar-nav-item-active' : 'sidebar-nav-item-inactive'
                            }`}
                            onClick={() => setSidebarOpen(false)}
                          >
                            <div className="flex items-center">
                              <IconComponent
                                className={`mr-3 h-5 w-5 ${
                                  isCurrentPage ? 'sidebar-nav-icon-active' : 'sidebar-nav-icon-inactive'
                                }`}
                              />
                              {item.name}
                            </div>
                            {item.badge && (
                              <span className="notification-badge">
                                {item.badge}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Static sidebar for desktop */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 bg-white border-r border-gray-200">
            {/* Logo */}
            <div className="flex items-center h-16 flex-shrink-0 px-4 bg-white border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="dashboard-stat-icon bg-gradient-to-br from-primary-600 to-indigo-700">
                  <AcademicCapIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <span className="text-xl font-bold gradient-text">SmartTag School</span>
                  <p className="text-xs text-gray-500 -mt-1">{portalName}</p>
                </div>
              </div>
            </div>
            
            {/* Navigation */}
            <div className="flex-1 flex flex-col overflow-y-auto custom-scrollbar">
              <nav className="flex-1 px-2 py-4">
                {navigationSections.map((section) => (
                  <div key={section.title} className="mb-6">
                    <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                      {section.title}
                    </h3>
                    <div className="space-y-1">
                      {section.items.map((item) => {
                        const IconComponent = item.icon;
                        const isCurrentPage = location.pathname === item.href;
                        
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`sidebar-nav-item ${
                              isCurrentPage ? 'sidebar-nav-item-active' : 'sidebar-nav-item-inactive'
                            }`}
                          >
                            <div className="flex items-center">
                              <IconComponent
                                className={`mr-3 h-5 w-5 ${
                                  isCurrentPage ? 'sidebar-nav-icon-active' : 'sidebar-nav-icon-inactive'
                                }`}
                              />
                              {item.name}
                            </div>
                            {item.badge && (
                              <span className="notification-badge">
                                {item.badge}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </nav>
              
              {/* Bottom section */}
              <div className="flex-shrink-0 border-t border-gray-200 p-4">
                <button 
                  onClick={() => toast.success('Settings coming soon!')}
                  className="btn btn-ghost w-full justify-start"
                >
                  <Cog6ToothIcon className="mr-3 h-5 w-5" />
                  Settings
                </button>
                <button 
                  onClick={logout}
                  className="btn btn-danger w-full justify-start mt-1"
                >
                  <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5" />
                  Sign out
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top nav */}
        <header className="relative z-10 flex-shrink-0 flex h-16 glass shadow-sm border-b border-gray-200/50">
          <button
            onClick={() => setSidebarOpen(true)}
            className="px-4 border-r border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 lg:hidden transition-colors"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          
          <div className="flex-1 px-6 flex justify-between items-center">
            {/* Left section - Search */}
            <div className="flex-1 flex items-center max-w-2xl">
              <div className={`relative w-full transition-all duration-200 ${searchFocused ? 'max-w-2xl' : 'max-w-lg'}`}>
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className={`h-5 w-5 transition-colors ${searchFocused ? 'text-primary-500' : 'text-gray-400'}`} />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder={getSearchPlaceholder(userRole)}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                  className={`form-input pl-10 pr-12 ${
                    searchFocused ? 'ring-2 ring-primary-500/20 border-primary-300' : ''
                  }`}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <kbd className="hidden sm:inline-flex items-center px-2 py-0.5 border border-gray-200 rounded text-xs font-sans text-gray-400 bg-gray-50">
                    ⌘K
                  </kbd>
                </div>

                {/* Search Results Dropdown */}
                <AnimatePresence>
                  {showSearchResults && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      className="dropdown-menu right-0 left-0 w-full max-h-80 overflow-y-auto"
                    >
                      {searchResults.length > 0 ? (
                        <>
                          <div className="px-4 py-2 border-b border-gray-100">
                            <p className="text-xs text-gray-500">
                              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
                            </p>
                          </div>
                          {searchResults.map((result) => {
                            const IconComponent = getSearchIcon(result.type);
                            return (
                              <Link
                                key={result.id}
                                to={result.href}
                                className="dropdown-item"
                                onClick={() => {
                                  setShowSearchResults(false);
                                  setSearchQuery('');
                                }}
                              >
                                <div className="flex-shrink-0 dashboard-stat-icon bg-gray-100">
                                  <IconComponent className="h-4 w-4 text-gray-600" />
                                </div>
                                <div className="ml-3 flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {result.title}
                                  </p>
                                  <p className="text-xs text-gray-500 truncate">
                                    {result.subtitle}
                                  </p>
                                </div>
                                <span className="badge badge-gray capitalize">
                                  {result.type}
                                </span>
                              </Link>
                            );
                          })}
                        </>
                      ) : (
                        <div className="px-4 py-8 text-center">
                          <p className="text-sm text-gray-500">No results found for "{searchQuery}"</p>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
            
            {/* Right section - Actions & Profile */}
            <div className="ml-4 flex items-center space-x-3">
              {/* Quick Actions */}
              <div className="hidden md:flex items-center space-x-2">
                <button 
                  onClick={() => toast.success('Terminal coming soon!')}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  <CommandLineIcon className="h-5 w-5" />
                </button>
                <button 
                  onClick={() => toast.success('Theme toggle coming soon!')}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  <SunIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Notifications */}
              <NotificationBell />
              
              {/* Profile Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className={`flex items-center space-x-3 p-2 rounded-xl transition-all duration-200 ${
                    profileDropdownOpen 
                      ? 'bg-blue-50 ring-2 ring-blue-500/20 border-blue-200' 
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="h-8 w-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-sm ring-2 ring-white">
                    <span className="text-sm font-bold text-white">
                      {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                    </span>
                  </div>
                  <div className="hidden md:block text-left">
                    <div className="text-sm font-medium text-gray-900">
                      {user?.firstName} {user?.lastName}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center space-x-1">
                      <span>{portalName.replace(' Portal', '')}</span>
                      <div className="w-1 h-1 bg-green-400 rounded-full"></div>
                    </div>
                  </div>
                  <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                    profileDropdownOpen ? 'rotate-180' : ''
                  }`} />
                </button>

                {/* Profile Dropdown Menu */}
                <AnimatePresence>
                  {profileDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      className="dropdown-menu right-0 w-64"
                      onMouseLeave={() => setProfileDropdownOpen(false)}
                    >
                      <div className="px-4 py-3 border-b border-gray-100">
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-sm">
                            <span className="text-sm font-bold text-white">
                              {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                            </span>
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {user?.firstName} {user?.lastName}
                            </div>
                            <div className="text-xs text-gray-500">{user?.email}</div>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className="badge badge-success text-xs">
                                {portalName.replace(' Portal', '')}
                              </span>
                              <div className="flex items-center text-xs text-gray-400">
                                <div className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1"></div>
                                Online
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="py-1">
                        <Link 
                          to={getProfileUrl(userRole)}
                          onClick={() => setProfileDropdownOpen(false)}
                          className="dropdown-item group"
                        >
                          <UserCircleIcon className="h-4 w-4 mr-3 text-gray-500 group-hover:text-blue-600" />
                          <div>
                            <p className="text-sm font-medium">My Profile</p>
                            <p className="text-xs text-gray-500">View and edit personal information</p>
                          </div>
                        </Link>
                        <Link 
                          to={get2FAUrl(userRole)}
                          onClick={() => setProfileDropdownOpen(false)}
                          className="dropdown-item group"
                        >
                          <ShieldCheckIcon className="h-4 w-4 mr-3 text-gray-500 group-hover:text-green-600" />
                          <div>
                            <p className="text-sm font-medium">Two-Factor Auth</p>
                            <p className="text-xs text-gray-500">Secure your account with 2FA</p>
                          </div>
                        </Link>
                        
                        <Link 
                          to={getSettingsUrl(userRole)}
                          onClick={() => setProfileDropdownOpen(false)}
                          className="dropdown-item group"
                        >
                          <Cog6ToothIcon className="h-4 w-4 mr-3 text-gray-500 group-hover:text-blue-600" />
                          <div>
                            <p className="text-sm font-medium">Account Settings</p>
                            <p className="text-xs text-gray-500">Notifications, security & preferences</p>
                          </div>
                        </Link>
                        
                        {/* Role-specific quick actions */}
                        {userRole === 'super_admin' && (
                          <Link 
                            to="/admin/analytics"
                            onClick={() => setProfileDropdownOpen(false)}
                            className="dropdown-item"
                          >
                            <DocumentTextIcon className="h-4 w-4 mr-3" />
                            Platform Analytics
                          </Link>
                        )}
                        
                        {(userRole === 'school_admin' || userRole === 'teacher') && (
                          <Link 
                            to={userRole === 'school_admin' ? '/schoolAdmin/analytics' : '/teacher/classes'}
                            onClick={() => setProfileDropdownOpen(false)}
                            className="dropdown-item"
                          >
                            <DocumentTextIcon className="h-4 w-4 mr-3" />
                            {userRole === 'school_admin' ? 'School Analytics' : 'My Classes'}
                          </Link>
                        )}
                        
                        <div className="border-t border-gray-100 my-1"></div>
                        
                        <button 
                          onClick={() => {
                            setProfileDropdownOpen(false)
                            toast.success('Help & Support coming soon!')
                          }}
                          className="dropdown-item w-full text-left"
                        >
                          <span className="h-4 w-4 mr-3 flex items-center justify-center">❓</span>
                          Help & Support
                        </button>
                        
                        <div className="border-t border-gray-100 my-1"></div>
                        <button 
                          onClick={() => {
                            setProfileDropdownOpen(false)
                            logout()
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-error-600 hover:bg-error-50 transition-colors"
                        >
                          <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3" />
                          Sign Out
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </header>

        {/* Main content area */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none custom-scrollbar">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="p-6"
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;